import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X, Check } from 'lucide-react'
import { Button as AriaButton, ListBox, ListBoxItem, Popover, Select, SelectValue } from 'react-aria-components'
import { getLanguageFlag, getLanguageDisplayName, SUPPORTED_LANGUAGES } from '@/lib/languageFlags'
import { cn } from '@/lib/utils'

interface LanguageSelectorProps {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  excludeLanguages?: string[]
}

export function LanguageSelector({
  value,
  onChange,
  placeholder = 'Select a language',
  disabled = false,
  className,
  excludeLanguages = [],
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const translatedLanguages = useMemo(() => {
    return SUPPORTED_LANGUAGES.map((lang) => ({
      ...lang,
      name: getLanguageDisplayName(lang.id, i18n.language, lang.name) || lang.name,
    }))
  }, [i18n.language])

  const availableLanguages = useMemo(() => {
    return translatedLanguages.filter((lang) => !excludeLanguages.includes(lang.id))
  }, [translatedLanguages, excludeLanguages])

  const selectedLanguage = translatedLanguages.find((lang) => lang.id === value)

  return (
    <Select
      isDisabled={disabled}
      selectedKey={value || null}
      onSelectionChange={(key) => onChange(key as string)}
      onOpenChange={setIsOpen}
      className={cn('relative', className)}
      aria-label={placeholder}
    >
      <AriaButton
        className={cn(
          'flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left shadow-sm transition-colors',
          'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
          'dark:border-gray-600 dark:bg-gray-800',
          disabled && 'cursor-not-allowed opacity-50',
          isOpen && 'border-primary-500 ring-2 ring-primary-500/20'
        )}
      >
        <SelectValue className="flex items-center gap-2">
          {selectedLanguage ? (
            <>
              <span className="text-lg leading-none">
                {getLanguageFlag(selectedLanguage.id)}
              </span>
              <span className="text-gray-900 dark:text-gray-100">
                {selectedLanguage.name}
              </span>
            </>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </SelectValue>
        <div className="flex items-center gap-1">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onChange('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  onChange('')
                }
              }}
              className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </AriaButton>

      <Popover
        className={cn(
          'z-50 w-[var(--trigger-width)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg',
          'dark:border-gray-700 dark:bg-gray-800',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox
          className="max-h-60 overflow-auto p-1 focus:outline-none"
          items={availableLanguages}
          aria-label={placeholder}
        >
          {(item) => (
            <ListBoxItem
              id={item.id}
              textValue={item.name}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'focus:bg-gray-100 dark:focus:bg-gray-700',
                'selected:bg-primary-50 selected:text-primary-700 dark:selected:bg-primary-900/30 dark:selected:text-primary-300'
              )}
            >
              {({ isSelected }) => (
                <>
                  <span className="text-lg leading-none">{getLanguageFlag(item.id)}</span>
                  <span className="flex-1 text-gray-900 dark:text-gray-100">
                    {item.name}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-primary-600" />}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  )
}

export default LanguageSelector
